import React, { createContext, useContext, useState, useEffect } from 'react';
import * as api from '../api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('tistore_user');
    return saved ? JSON.parse(saved) : null;
  });
  useEffect(() => {
    const saved = localStorage.getItem('tistore_user');
    if (saved) {
      try { const account = JSON.parse(saved); api.getUser(account.id).then(setUser).catch(error => { if (error.response?.status === 401 || error.response?.status === 403) { setUser(null); localStorage.removeItem('tistore_user'); } }); } catch { setUser(null); }
    }
  }, []);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterMode, setIsRegisterMode] = useState(false);

  // Синхронизация с localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem('tistore_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('tistore_user');
    }
  }, [user]);

  // Удобная функция для открытия модалки входа
  const openLoginModal = () => {
    setIsRegisterMode(false); // всегда открываем в режиме "вход"
    setIsLoginModalOpen(true);
  };

  // Обновление данных пользователя с сервера
  const refreshUser = async () => {
    if (!user?.id) return;
    try {
      const userData = await api.getUser(user.id);
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  // Вход
  const login = async (email, password) => {
    try {
      const userData = await api.loginUser({ email, password });
      setUser(userData);
      setIsLoginModalOpen(false);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Ошибка входа' };
    }
  };

  const authError = error => typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Проверьте email и пароль (не менее 8 символов).';
  const register = async (email, password, name, phone) => {
    try {
      return { success: true, ...await api.registerUser({ email, password, name, phone, privacy_consent: true, consent_version: "2026-10-18" }) };
    } catch (error) { return { success: false, error: authError(error) }; }
  };
  const confirmRegistration = async (challenge_id, code) => {
    try {
      const account = await api.verifyRegistration({ challenge_id, code });
      setUser(account);
      setIsLoginModalOpen(false);
      return { success: true };
    } catch (error) { return { success: false, error: authError(error) }; }
  };

  // Выход
  const logout = async () => {
    await api.logoutUser();
    setUser(null);
    localStorage.removeItem('tistore_user');
  };

  // Обновление монет (например, после заказа)
  const updateCoins = (amount) => {
    setUser(prev => prev ? { ...prev, coins: (prev.coins || 0) + amount } : null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoginModalOpen,
        setIsLoginModalOpen,
        isRegisterMode,
        setIsRegisterMode,
        openLoginModal, // ← новая удобная функция
        login,
        register,
        confirmRegistration,
        logout,
        updateCoins,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};